"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createWorkspaceRequestSchema = exports.WORKSPACE_TIMEZONES = exports.workspaceSummarySchema = exports.demoDataRemovedSchema = exports.demoDataSummarySchema = exports.requestWorkspaceDeletionSchema = exports.slugAvailabilitySchema = exports.updateWorkspaceRequestSchema = exports.workspaceSettingsSchema = exports.RESERVED_WORKSPACE_SLUGS = exports.WORKSPACE_SLUG = exports.WORKSPACE_LANGUAGES = void 0;
exports.slugIsReserved = slugIsReserved;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const text_1 = require("../text");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Workspace settings — C16's Általános and Adatok sections.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `markup.html` 1750–1830 (general) and 2118–2167 (data). Read the header of
 * `0022_workspace_settings.sql` first: it records which of the mockup's seven general fields landed
 * and why the other two did not.
 */
/** The mockup's `wsLangOptions`. Same two as `members.language`. */
exports.WORKSPACE_LANGUAGES = ['hu', 'en'];
/**
 * ⚠️ **The slug's shape is 0003's CHECK, restated so a form can refuse before the round trip.** A
 * regex in two places is a duplication worth having: the alternative is a `23514` constraint
 * violation rendered as *"something went wrong"*.
 */
exports.WORKSPACE_SLUG = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The slugs a workspace may not have, because a route already answers to   │
 * │ them. R-38.                                                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS EXISTS BECAUSE THE SLUG IS NOW THE FIRST SEGMENT OF EVERY ADDRESS IN THE PRODUCT.**
 * `/{workspace}/tasks`, `/{workspace}/settings`, and so on. react-router ranks a static segment
 * above a dynamic one, so a literal route always wins — which means a workspace slugged `sign-in`
 * is not a routing *conflict*, it is a workspace **nobody can ever open**, with no error and
 * nothing to diagnose: every one of its addresses renders the sign-in screen.
 *
 * ⚠️⚠️ **THE LIST IS EVERY PATH THAT STAYS AT THE ROOT, PLUS THE TWO nginx OWNS.** The
 * signed-out screens keep their bare addresses because a person reaching them has no workspace to
 * name — and `api` and `assets` are `web`'s own `location` blocks, so a workspace called either
 * would have its every request proxied to the API or served as a static file.
 *
 * ⚠️ **`apps/web/src/__tests__/routes.test.tsx` asserts this list covers every root route**, so a
 * new signed-out screen cannot ship without either joining the list or being caught. Keeping the
 * two in step by memory is exactly the failure the reachability gate exists to prevent.
 *
 * ⚠️ **And `supabase/migrations/0098` repeats it as a CHECK constraint**, because a slug that
 * reaches the database is one no client validated. `apps/api/src/workspace/__tests__/
 * reserved-slugs.test.ts` reads the migration and fails when the two disagree — the same
 * mechanism `entrypoint.test.ts` uses to pin nginx against the controller.
 */
exports.RESERVED_WORKSPACE_SLUGS = [
    /* `web`'s own nginx locations — see apps/web/nginx.conf. */
    'api',
    'assets',
    /* Every route that stays at the root, from apps/web/src/routes.tsx. */
    'auth',
    'forgot-password',
    'invite',
    /* C52 · `/oauth/consent`, which stays at the root because a person approving an application may
       have to choose which workspace they are approving it for. */
    'oauth',
    'reset-password',
    'sign-in',
    'sign-up',
    'specimen',
    'suspended',
    'welcome',
];
/**
 * Whether a slug is one of the reserved words.
 *
 * ⚠️ Case-insensitive and trimmed, because the column is `citext` and a form's value is whatever
 * somebody typed. `Sign-In` is the same reserved word as `sign-in`.
 */
function slugIsReserved(slug) {
    return exports.RESERVED_WORKSPACE_SLUGS.includes(slug.trim().toLowerCase());
}
exports.workspaceSettingsSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `workspace_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('workspace'),
    name: zod_1.z.string(),
    slug: zod_1.z.string(),
    /**
     * Defaults an invitation copies into the new member.
     *
     * ⚠️ **Not workspace-wide display settings.** `members.language` and `members.timezone` are what
     * anything reads; these seed them, and the consumer is C17's invite path. The mockup's own card
     * says as much — *"meghíváskor ezt kapja mindenki"*.
     */
    defaultLanguage: zod_1.z.enum(exports.WORKSPACE_LANGUAGES),
    defaultTimezone: zod_1.z.string(),
    /**
     * ⚠️⚠️ **THE WORKSPACE'S COUNTRY — C42, P3-4.** Unlike the two above it this is NOT a default an
     * invitation copies: it is a fact about the workspace, and it is the key the public-holiday
     * import runs on. Nager.Date is queried per country and year, so a calendar without one is
     * meaningless.
     *
     * ⚠️ **One country per workspace, deliberately.** A tenant operating in two countries runs two
     * workspaces, which is what workspaces are for; a per-person country would make a leave balance
     * depend on where somebody sits, and nothing asks for that.
     */
    country: zod_1.z.string(),
    /**
     * ⚠️⚠️ **WHEN THIS WORKSPACE STARTED, AND THE HOLIDAY YEAR PICKER IS WHY IT IS HERE.** That
     * picker offered this year and the three after it, on the argument that a past year is a calendar
     * nobody can change usefully. Corrected on 15 Sep: *"it should also show past years assuming the
     * tenant was live back then"* — a workspace that has been running since 2024 has three years of
     * real calendar behind it, and not being able to look at them is the loss, not the edit.
     *
     * ⚠️ **The workspace's own date, not the tenant's.** A second workspace opened this year has no
     * calendar before this year whatever the tenant has been doing.
     */
    createdAt: zod_1.z.string(),
    /**
     * ⚠️ **WHETHER THIS WORKSPACE SENDS NOTIFICATION EMAILS AT ALL** (`0195`). Off: the bell keeps
     * working and no notification email goes out — whatever each member's own matrix says.
     * Invitations, the per-user security emails and a requested export are not notifications and
     * still go. An addition (rule 8): the mockup draws no such setting.
     */
    notificationEmails: zod_1.z.boolean(),
    /**
     * ⚠️ **A deletion REQUEST, not a deletion.** Non-null means somebody asked; nothing has been
     * destroyed, and the workspace keeps working. `0022` records why a hard delete is not behind a
     * button.
     */
    deletionRequestedAt: zod_1.z.string().nullable(),
    deletionRequestedByName: zod_1.z.string().nullable(),
    /** Facts the danger zone quotes back, so the confirm dialog says what is at stake. */
    projectCount: zod_1.z.number().int().nonnegative(),
    taskCount: zod_1.z.number().int().nonnegative(),
    memberCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️ **Every field optional but the version**, because the section has two independent cards and a
 * reset button per card — the same interaction model `updatePersonRequestSchema` records. Sending the
 * whole object back would make one card's Save overwrite the other's unsaved edits.
 */
exports.updateWorkspaceRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(200).optional(),
    slug: zod_1.z.string().regex(exports.WORKSPACE_SLUG).optional(),
    defaultLanguage: zod_1.z.enum(exports.WORKSPACE_LANGUAGES).optional(),
    defaultTimezone: zod_1.z.string().min(1).max(64).optional(),
    /** ⚠️ ISO 3166-1 alpha-2, the same expression the column checks. */
    country: zod_1.z
        .string()
        .regex(/^[A-Z]{2}$/)
        .optional(),
    notificationEmails: zod_1.z.boolean().optional(),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * The slug check — the mockup's `checkSlug`.
 *
 * ⚠️⚠️ **`available` IS NOW GLOBAL AND CHECKED AGAINST A RESERVED LIST, WHICH IS WHAT THE MOCKUP
 * ALWAYS DID.** This comment used to record the opposite as a deliberate difference: the mockup
 * asks whether `{slug}.forecastify.io` is free against a global reserved list, and ours asked only
 * whether the slug was free *within this tenant*, because that was 0003's `unique (tenant_id,
 * slug)`. R-38 made the slug the first segment of every address in the product, which needs both
 * halves of the mockup's version — a slug that is not unique cannot address one workspace, and one
 * that shadows a root route addresses a screen instead. Nothing serves a subdomain, so the screen
 * still shows no domain.
 *
 * ⚠️ `available` is true for the workspace's OWN current slug — otherwise a form that has not
 * changed the field would report it taken.
 *
 * ⚠️⚠️ **`reason` EXISTS BECAUSE "TAKEN" IS A LIE ABOUT A RESERVED WORD.** Telling somebody that
 * `settings` is taken sends them looking for who took it; the answer is that nobody can have it.
 * Absent when `available` is true, so the two fields cannot disagree.
 */
exports.slugAvailabilitySchema = zod_1.z.object({
    slug: zod_1.z.string(),
    available: zod_1.z.boolean(),
    reason: zod_1.z.enum(['taken', 'reserved']).optional(),
});
/**
 * Requesting deletion.
 *
 * ⚠️⚠️ **THE TYPED NAME IS PART OF THE REQUEST, NOT JUST OF THE DIALOG.** The mockup gates its
 * button on the name matching; if only the client checked, the endpoint would delete a workspace on
 * a bare `POST` from anywhere. A confirmation the server cannot verify is not a confirmation.
 */
exports.requestWorkspaceDeletionSchema = zod_1.z.object({
    /** Must equal the workspace's current name, exactly. */
    confirmName: zod_1.z.string(),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-5 · the first-run demo, and removing it.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `GET /workspace/demo` answers what a removal would do, without doing it — the confirmation's
 * *"12 items you changed will stay"*. `DELETE /workspace/demo` does it and answers what it did.
 * Both need `settings.manage`.
 *
 * ⚠️ **Counts of rows, not of things a person would name.** A kept time entry keeps its task, line
 * item, contract, project and client — five rows the person did not touch themselves — so the
 * screen words the kept figure as "items that stay", never as "your changes".
 */
exports.demoDataSummarySchema = zod_1.z.object({
    /** Rows a removal would delete. `0` means the workspace holds no demo data. */
    removable: zod_1.z.number().int().nonnegative(),
    /** Demo rows that would stay, because somebody changed them or something of theirs needs them. */
    kept: zod_1.z.number().int().nonnegative(),
});
exports.demoDataRemovedSchema = zod_1.z.object({
    removed: zod_1.z.number().int().nonnegative(),
    kept: zod_1.z.number().int().nonnegative(),
});
/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * C42 · A second workspace in a tenant, and the switcher that needs one
 * ───────────────────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **The mockup carries `WORKSPACES` and draws neither the switcher nor the create flow** — one of
 * the 140 its markup does not render. So `docs/design-system/` decides the screens; what the mockup
 * settles is the model: a workspace has a name and an initial, and somebody moves between them.
 *
 * ⚠️⚠️ **THE ICON IS AN ORDINARY ATTACHMENT, NOT A COLUMN.** `file_target.workspace_icon` (`0061`) —
 * the same table, scan pipeline and download route as a user's avatar. So nothing here carries a
 * URL, only whether one exists; the bytes come from `/files/workspace-icon/:id`, which is the avatar
 * route's twin for the avatar route's reason: an `<img src>` cannot carry an Authorization header.
 */
exports.workspaceSummarySchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    slug: zod_1.z.string(),
    /**
     * ⚠️ **New on the wire, and `app.tsx` has been waiting for it since C22.** Its comment reads
     * *"a workspace has no name on the wire, so inventing one here would be inventing data — it gains
     * a name when the workspace read does."* `0062` is that read.
     */
    name: zod_1.z.string(),
    tenantId: zod_1.z.uuid(),
    memberId: zod_1.z.uuid(),
    /** ⚠️ A boolean, not a URL. The shell draws an `<img>` or an initial; see above. */
    hasIcon: zod_1.z.boolean(),
});
/**
 * ⚠️ **The same six the settings screen offers**, and deliberately not the IANA database: a picker
 * with six hundred entries is one nobody finds Budapest in. A workspace needing a seventh is a
 * support request we would rather have than a search box.
 */
exports.WORKSPACE_TIMEZONES = [
    'Europe/Budapest',
    'Europe/Berlin',
    'Europe/London',
    'Europe/Bucharest',
    'America/New_York',
    'UTC',
];
exports.createWorkspaceRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(80),
    /**
     * ⚠️ **Optional, because the wizard does not ask.** The three fields are name, icon and timezone;
     * a slug is a URL detail nobody wants to think about while naming a workspace. The server derives
     * one and **suffixes it if taken** (`0062`), so this exists only for a caller with a reason.
     */
    slug: zod_1.z.string().trim().min(text_1.SLUG_MIN).max(text_1.SLUG_MAX).optional(),
    language: zod_1.z.enum(exports.WORKSPACE_LANGUAGES).default('hu'),
    timezone: zod_1.z.enum(exports.WORKSPACE_TIMEZONES),
});
//# sourceMappingURL=contract.js.map